import {
  CITIES,
  DEFAULT_CITY_RADIUS_KM,
  type City,
} from "@/config/locations";
import { calculateDistance } from "@/lib/api/products/geo";

/**
 * 도시 소속 판정에 쓰는 지점 값. 좌표 + (있으면) 지점의 geo 코드
 * (branch_info.geo_admin1_code · geo_admin2_code · geo_flag).
 */
export type CityMemberBranch = {
  latitude: number | null;
  longitude: number | null;
  admin1?: string | null;
  admin2?: string | null;
  /** geo 판정 경고(주소 불일치 등) — 있으면 코드를 믿지 않고 반경으로 본다 */
  geoFlag?: string | null;
};

type ProductBackedBranch = CityMemberBranch & { id: string };

/**
 * Maps approved, active pickup branches to their canonical search city.
 *
 * The sitemap must describe places customers can actually rent from, rather
 * than every destination stored in the UI search dictionary.
 */
export function getProductBackedCityIds(
  branches: ProductBackedBranch[],
  cities: City[] = CITIES,
): Set<string> {
  const cityIds = new Set<string>();
  for (const branch of branches) {
    for (const cityId of cityIdsForBranch(branch, cities)) cityIds.add(cityId);
  }
  return cityIds;
}

/** 도시 기본 검색 박스(getCityBounds 와 같은 사각형: 중심 ± radius_km/111°) 안인가. */
function insideDefaultBox(city: City, lat: number, lng: number): boolean {
  if (!city.coordinates) return false;
  const off = (city.radius_km ?? DEFAULT_CITY_RADIUS_KM) / 111;
  return (
    Math.abs(lat - city.coordinates.latitude) <= off &&
    Math.abs(lng - city.coordinates.longitude) <= off
  );
}

/** 중심 반경 원 안 또는 기본 검색 박스 안 — 예전 두 판정(재고=원, 목록=박스)의 합집합. */
function insideRadius(city: City, lat: number, lng: number): boolean {
  if (!city.coordinates) return false;
  const radiusKm = city.radius_km ?? DEFAULT_CITY_RADIUS_KM;
  return (
    calculateDistance(city.coordinates.latitude, city.coordinates.longitude, lat, lng) <= radiusKm ||
    insideDefaultBox(city, lat, lng)
  );
}

/**
 * 지점이 도시에 속하는가 — 도시 랜딩·도시 × 품목 집계·사이트맵·허브 범위·상품 브레드크럼이
 * 모두 이 판정 하나를 쓴다(10-19).
 *
 *  - 행정구역: 지점의 시군구(admin2)가 도시 목록에 있거나, 시도(admin1)가 목록에 있고 제외
 *    시군구가 아니면 소속. 일본 도쿄 = 23구, 한국 서울 = 서울특별시 전체.
 *  - 반경: 도시 중심 반경 원 또는 기본 검색 박스 안. 기존 도시는 행정구역 ∪ 반경이라 지금
 *    랜딩에 잡히던 지점이 빠지지 않는다. 새 여행지(radius: false)는 행정구역만 — 원이 이웃
 *    도시를 침범하지 않게.
 *  - 지점에 geo 코드가 없거나 geo 경고가 있으면 코드를 믿지 않고 반경으로만 본다.
 */
export function branchInCity(city: City, branch: CityMemberBranch): boolean {
  const { latitude: lat, longitude: lng } = branch;
  const located = lat != null && lng != null;
  const membership = city.membership;
  const coded = !branch.geoFlag && Boolean(branch.admin2 || branch.admin1);
  if (membership && coded) {
    const excluded = !!branch.admin2 && (membership.excludeAdmin2?.includes(branch.admin2) ?? false);
    if (!excluded) {
      if (branch.admin2 && membership.admin2?.includes(branch.admin2)) return true;
      if (branch.admin1 && membership.admin1?.includes(branch.admin1)) return true;
    }
  }
  if (!located) return false;
  const radiusAllowed = !membership || membership.radius !== false || !coded;
  return radiusAllowed && insideRadius(city, lat, lng);
}

/** 지점이 속한 도시 id 들(겹칠 수 있다 — 도쿄 반경 ∩ 다마). */
export function cityIdsForBranch(
  branch: CityMemberBranch,
  cities: City[] = CITIES,
): string[] {
  const ids: string[] = [];
  for (const city of cities) {
    if (branchInCity(city, branch)) ids.push(city.id);
  }
  return ids;
}
